import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useUserContext } from '../hooks/useUserContext';
import userService from '../services/userService';
import appConfigService, { type WelcomeVideoConfig } from '../services/appConfigService';

/**
 * One-time welcome video shown the first time a new user lands in the app
 * after completing account creation. Dismissing it persists
 * `hasSeenWelcomeVideo` on the user so it never shows again.
 */
export default function WelcomeVideoDialog() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { loading, backendProfile } = useUserContext();
  const [dismissed, setDismissed] = useState(false);
  const [videoConfig, setVideoConfig] = useState<WelcomeVideoConfig | null>(null);

  const shouldShow = !dismissed && !loading && backendProfile?.hasSeenWelcomeVideo === false;

  useEffect(() => {
    if (!shouldShow) return;
    appConfigService.getAppConfig()
      .then((cfg) => setVideoConfig(cfg.welcomeVideo))
      .catch(() => {
        // If config fetch fails, skip the video rather than blocking the user.
      });
  }, [shouldShow]);

  const open = shouldShow && videoConfig != null && videoConfig.enabled && videoConfig.embedUrl !== '';

  const handleClose = () => {
    setDismissed(true);
    userService.markWelcomeVideoSeen().catch((error) => {
      // Non-fatal — worst case the video shows again on next login.
      console.warn('Failed to mark welcome video as seen:', error);
    });
  };

  if (!open) return null;

  return (
    <Dialog open onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
          fontWeight: 700,
        }}
      >
        Welcome to CPAx Academy!
        <IconButton aria-label="Close" onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Watch this quick video to learn how to get the most out of your account.
        </Typography>
        <Box
          component="iframe"
          src={videoConfig.embedUrl}
          title="Welcome to CPAx Academy"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          sx={{
            width: '100%',
            aspectRatio: '16 / 9',
            border: 0,
            borderRadius: 1,
            display: 'block',
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={handleClose}>
          Get Started
        </Button>
      </DialogActions>
    </Dialog>
  );
}
